import React, { useState, useRef, useEffect, useCallback } from 'react';
import { executeMutation } from '../../graphql';
import { getAddressSuggestions } from '../../graphql/mutations.js';
import { usePageContent } from '../../hooks/usePageContent';

const AddressLookup = ({ value, onChange, onSelect, onBlur, error, id = 'address-lookup' }) => {
  const { find } = usePageContent('address-lookup');
  const c = find('addressLookup') || {};

  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const listboxId = `${id}-listbox`;

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setSearching(true);
    try {
      const result = await executeMutation(getAddressSuggestions, { input: { SearchTerm: query } });
      const items = result?.getAddressSuggestions?.AddressList || [];
      setSuggestions(Array.isArray(items) ? items : []);
      setShowDropdown(true);
      setActiveIndex(-1);
    } catch (err) {
      console.error('Address suggestions error:', err);
      setSuggestions([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const getDisplayText = (suggestion) => suggestion.Address || '';

  const handleSelect = (suggestion) => {
    const display = getDisplayText(suggestion);
    onChange(display);
    onSelect(suggestion, display);
    setShowDropdown(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        className="input__ctrl input__ctrl--two-col input__ctrl--structured-address"
        type="text"
        id={id}
        name="address"
        placeholder={c.placeholder || 'E.g. 49 Smith Ave, Melbourne VIC 3000'}
        value={value}
        onChange={handleInputChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown && suggestions.length > 0}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
      />
      {searching && (
        <span
          style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#666', pointerEvents: 'none' }}
          aria-live="polite"
        >
          {c.searchingText || 'Searching...'}
        </span>
      )}
      {showDropdown && suggestions.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={c.suggestionsLabel || 'Address suggestions'}
          style={{
            position: 'absolute',
            zIndex: 100,
            left: 0,
            right: 0,
            top: '100%',
            background: '#fff',
            border: '1px solid #bbb',
            borderTop: 'none',
            listStyle: 'none',
            margin: 0,
            padding: 0,
            maxHeight: '240px',
            overflowY: 'auto',
            boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
          }}
        >
          {suggestions.map((s, i) => (
            <li
              key={i}
              id={`${id}-option-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                fontSize: '16px',
                lineHeight: '1.4',
                borderBottom: '1px solid #eee',
                background: i === activeIndex ? '#f0f5ee' : '#fff',
              }}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {getDisplayText(s)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressLookup;
